import env from '~/libs/env'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRootStore } from '~/store/root'
import { useApplicationConfigStore } from '~/store/application-config'
import { sessionStorageService } from '~/libs/session-storage.service'
import appConstants from '~/libs/constants/application-constants.js'

const { nuxtBaseUrl } = appConstants

export const useContactGuestStore = defineStore('contactGuest', () =>{
  const { $axios, $lendioCookies } = useNuxtApp()
  const route = useRoute()
  const rootStore = useRootStore()
  const appConfigStore = useApplicationConfigStore()

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
  */
  const initialBusinessName = 'your business'
  const businessName = ref(initialBusinessName)
  const initialPrimaryOwnerName = 'the original applicant'
  const primaryOwnerName = ref(initialPrimaryOwnerName)
  const secureLink = ref('')
  const modules = ref([])
  const contactFirst = ref('')
  const contactLast = ref('')
  const token = ref('')
  const completed = ref(false)
  const initialized = ref(false)

  /*
  ██████   ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
  ██████   ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
  */
  const _parseLink = (_secureLink) => {
    try {
      JSON.parse(atob(_secureLink))
    } catch (e) {
      return false
    }
    return true
  }

  const affiliateIdFromStorage = computed(() => {
    const ssAffiliateId = $lendioCookies.get('guestAffiliateId', { tenantAgnostic: true })

    if (!ssAffiliateId || ssAffiliateId === '') {
      return ssAffiliateId ? ssAffiliateId : null
    }

    return rootStore.affiliateId
  })

  const secureLinkFromStorage = computed(() => {
    if (!secureLink.value || secureLink.value === '') {
      const ssSecureLink = sessionStorageService.getItem('guestSecureLink')
      return ssSecureLink ? JSON.parse(ssSecureLink) : []
    }

    return secureLink.value
  })

  const businessNameFromStorage = computed(() => {
    if (!businessName.value || businessName.value === initialBusinessName) {
      const cBusinessName = sessionStorageService.getItem('guestBusinessName')
      return cBusinessName
    }

    return businessName.value
  })

  const primaryOwnerNameFromStorage = computed(() => {
    if (!primaryOwnerName.value || primaryOwnerName.value === initialPrimaryOwnerName) {
      const cPrimaryOwnerName = sessionStorageService.getItem('guestPrimaryOwnerName')
      return cPrimaryOwnerName
    }

    return primaryOwnerName.value
  })

  const getIsBadSecureLink = computed(() => {
    // A secure link is considered bad if it does not contain a period in the center of a string containing numbers on the left side and anything on the right side
    const _value = secureLink.value

    if (!_value || _value === '') {
      return true
    }

    // Check if the secure link is a string that contains a period in the middle
    if (!_value.includes('.') || _value.split('.').length !== 2) {
      return true
    }

    // Check if the left side of the period is a number
    const leftSide = _value.split('.')[0]
    if (isNaN(leftSide) || leftSide === '') {
      return true
    }

    // Check if the right side of the period is not empty
    const rightSide = _value.split('.')[1]
    if (rightSide === '') {
      return true
    }

    return false
  })

  const getSecureLinkId = computed(() => {
    if (!secureLink.value || secureLink.value === '') {
      return ''
    }

    if (getIsBadSecureLink.value) {
      return ''
    }

    return secureLink.value ? secureLink.value.split('.')[0] : ''
  })

  const isGuest = computed(() => {
    return route.path.startsWith('/guest')
  })

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
  */

  async function submitMfaCode ( mfaCode, verificationPath ) {
    if (!secureLink.value || secureLink.value === '') {
      return { errored: true, error: 'Secure link is not set.' }
    }

    if (!verificationPath || verificationPath === '') {
      return { errored: true, error: 'Verification path is not set.' }
    }


    return $axios.post(`${env('apiUrl')}${verificationPath}`,
        {
          accessCode: mfaCode, secureLink: secureLink.value
        }
      )
      .then(response => {
        const data = response?.data?.data

        completed.value = data?.completed || false
        contactFirst.value = data?.contactFirst || contactFirst.value
        contactLast.value = data?.contactLast || contactLast.value
        businessName.value = data?.businessName || businessName.value
        primaryOwnerName.value = data?.primaryOwnerName || primaryOwnerName.value
        modules.value = data?.modules || []
        token.value = data?.token || ''

        return response?.data
      })
      .catch(error => {
        console.log(error)
        return { errored: true, error }
      })
  }

  async function getContactPostOfferDocumentModules () {
    const _token = token.value
    if (!_token || _token === '') {
      return { errored: true, error: 'Token is not set.' }
    }
    $lendioCookies.set('contactGuestDocUploadToken', _token, { path: `${nuxtBaseUrl}/`, expires: 0.25 })
    try {
      return await appConfigStore.getDocumentUploadRequestApplicationConfig(_token, true)
    } catch (e) {
      return { errored: true, error: e }
    }
  }

  async function updateContactAnswers ({ answers }) {
    if (!token.value || token.value === '') {
      return { errored: true, error: 'Token is not set.' }
    }

    let _answers = {}
    const personalTaxAlias = 'currentYearPersonalTaxReturnSubmitted'
    if (Object.keys(answers).some(key => key.includes(personalTaxAlias))) {
      _answers = {
        filed: answers[personalTaxAlias]
      }

      const result = await $axios.post(`${env('apiUrl')}/contact/guest/additional-owners/tax-returns/status`, {
        ..._answers,
        }, { headers: { 'Authorization': `lendio-jwt ${token.value}` } })
          .then(response => {
            const data = response?.data?.data

            // Update modules with the new data to ensure tax return module up to date
            modules.value = data?.modules || modules.value
            return response?.data
          })
          .catch(error => {
            return { errored: true, error }
          })

      if (result?.errored) {
        return result
      }

      return [ personalTaxAlias ]
    }

    return { errored: true, error: 'No tax return answers provided.' }
  }

  async function updatePersonalInfoAnswers ({ answers }) {
    if (!token.value || token.value === '') {
      return { errored: true, error: 'Token is not set.' }
    }

    return $axios.put(`${env('apiUrl')}/contact/guest/additional-owners`, {
      ...answers,
    }, { headers: { 'Authorization': `lendio-jwt ${token.value}` } })
      .then(response => {
        return response?.data
      })
      .catch(error => {
        console.log(error)
        return { errored: true, error }
      })
  }

  async function markGuestComplete () {
    if (!token.value || token.value === '') {
      return { errored: true, error: 'Token is not set.' }
    }

    return $axios.post(`${env('apiUrl')}/contact/guest/additional-owners/completed`,
      {},
      { headers: { 'Authorization': `lendio-jwt ${token.value}` } }
    )
      .then(response => {
        return response?.data
      })
      .catch(error => {
        console.log(error)
        return { errored: true, error }
      })
  }

  async function loadContactAnswersFromStorage () { return null }

  function resetValues() {
    businessName.value = 'your business'
    primaryOwnerName.value = 'the original applicant'
    contactFirst.value = ''
    contactLast.value = ''
    modules.value = []
    token.value = ''
  }
  function loadFromStorage() {
    secureLink.value = secureLinkFromStorage.value || ''
    businessName.value = businessNameFromStorage.value || businessName.value
    primaryOwnerName.value = primaryOwnerNameFromStorage.value || primaryOwnerName.value
    rootStore.setAffiliateId(affiliateIdFromStorage.value)

    if (secureLink.value === '') {
      // Wipe out affiliate ID cookie if no secure link is loaded
      // so that the cookie "expires" when the session storage does
      $lendioCookies.set('guestAffiliateId', rootStore.affiliateId, { tenantAgnostic: true })
    }
  }

  function loadTokenFromCookies() {
    if (token.value) {
      return // Do not override existing token if one is in memory
    }
    token.value = $lendioCookies.get('contactGuestDocUploadToken') || token.value
  }

  function initializeValues ({ _businessName, _primaryOwnerName, _secureLink, _affiliateId }) {
    if (_secureLink) {
      this.resetValues()

      sessionStorageService.setItem('guestSecureLink', JSON.stringify(_secureLink))
      secureLink.value = _secureLink
    } else {
      loadFromStorage()
    }
    rootStore.loadIsGuest(secureLink.value)

    if (_businessName) {
      sessionStorageService.setItem('guestBusinessName', _businessName)
      businessName.value = _businessName
    }

    if (_affiliateId) {
      $lendioCookies.set('guestAffiliateId', _affiliateId, { tenantAgnostic: true })
      rootStore.setAffiliateId(_affiliateId)
    }

    if (_primaryOwnerName) {
      sessionStorageService.setItem('guestPrimaryOwnerName', _primaryOwnerName)
      primaryOwnerName.value = _primaryOwnerName
    }

    initialized.value = true
  }

  return {
    // State
    businessName,
    completed,
    contactFirst,
    contactLast,
    modules,
    primaryOwnerName,
    token,
    secureLink,
    initialized,

    // Getters
    getSecureLinkId,
    getIsBadSecureLink,
    isGuest,

    // Actions
    initializeValues,
    resetValues,
    submitMfaCode,
    loadTokenFromCookies,
    getContactPostOfferDocumentModules,
    updatePersonalInfoAnswers,
    updateContactAnswers,
    loadContactAnswersFromStorage,
    markGuestComplete,
  }
})
